import { useState } from "react";
import {
  CATEGORIES,
  CATEGORY_LABEL,
  apiMessage,
  createFleetCost,
  updateFleetCost,
  type Category,
  type FleetCost,
  type Recurrence,
} from "../../../../../features/fleetCosts/fleetCosts";
import { toLocalInputValue } from "../../../../../pages/reports/utils/reportDateFormat";

interface Props {
  companyId: string;
  cost: FleetCost | null;
  units: { id: string; label: string }[];
  isDarkMode: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Fecha local: con toISOString, después de las 18:00 en México saldría la de mañana.
const today = () => toLocalInputValue(new Date()).slice(0, 10);

export default function FleetCostModal({ companyId, cost, units, isDarkMode, onClose, onSaved }: Props) {
  const [vehicleId, setVehicleId] = useState(cost?.vehicle_id ?? units[0]?.id ?? "");
  const [category, setCategory] = useState<Category>(cost?.category ?? "combustible");
  const [concept, setConcept] = useState(cost?.concept ?? "");
  const [amount, setAmount] = useState(cost ? String(cost.amount) : "");
  // Al editar un recurrente se edita el gasto original (su fecha de inicio), no esta aparición.
  const [date, setDate] = useState((cost?.date ?? today()).slice(0, 10));
  const [recurrence, setRecurrence] = useState<Recurrence>(cost?.recurrence ?? null);
  const [note, setNote] = useState(cost?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountN = Number(amount);
  const valid = vehicleId && amountN > 0 && date;

  const panel = isDarkMode ? "bg-dark-card text-white border-dark-border" : "bg-white text-gray-900 border-gray-200";
  const input = `w-full rounded-md border px-2 py-2 text-sm ${isDarkMode ? "bg-white/5 border-dark-border" : "bg-white border-gray-300"}`;
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  const save = async () => {
    setSaving(true);
    setError(null);
    // Mediodía local: la fecha no se corre de día al pasarla a UTC.
    const body = { vehicle_id: vehicleId, category, concept, amount: amountN, date: new Date(`${date}T12:00:00`).toISOString(), recurrence, note };
    try {
      if (cost) await updateFleetCost(companyId, cost.id, body);
      else await createFleetCost(companyId, body);
      onSaved();
    } catch (e) {
      setError(apiMessage(e, "No se pudo guardar el gasto."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="gasto-titulo" onClick={onClose}>
      <div className={`w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border shadow-2xl p-5 ${panel}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 id="gasto-titulo" className="text-lg font-semibold">{cost ? "Editar gasto" : "Agregar gasto"}</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className={`text-xl ${muted}`}>
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm sm:col-span-2">
            Unidad
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className={input}>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Categoría
            <select value={category} onChange={(e) => setCategory(e.target.value as Category)} className={input}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Concepto
            <input value={concept} maxLength={80} onChange={(e) => setConcept(e.target.value)} placeholder="Ej. Seguro, diésel, llantas" className={input} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Monto (MXN)
            <input type="number" min={0} step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${input} tabular-nums`} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Fecha
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-1 text-sm sm:col-span-2">
            ¿Se repite?
            <select value={recurrence ?? ""} onChange={(e) => setRecurrence((e.target.value || null) as Recurrence)} className={input}>
              <option value="">No</option>
              <option value="month">Cada mes</option>
              <option value="year">Cada año</option>
            </select>
            <span className={`text-xs ${muted}`}>Un gasto que se repite se captura una vez y aparece en cada periodo (p. ej. el seguro anual).</span>
          </label>
          <label className="flex flex-col gap-1 text-sm sm:col-span-2">
            Nota
            <textarea value={note} maxLength={500} rows={2} onChange={(e) => setNote(e.target.value)} className={input} />
          </label>
        </div>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className={`text-sm px-4 py-2 rounded-md border ${isDarkMode ? "border-dark-border" : "border-gray-300"}`}>
            Cancelar
          </button>
          <button type="button" disabled={!valid || saving} onClick={save} className="text-sm px-4 py-2 rounded-md bg-primary-500 text-white disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
